import {
  useCreate,
  useDelete,
  useDetail,
  useList,
  useUpdate,
  usePartialUpdate,
  useAction,
  usePlainList,
  fetchApi,
} from "@inmagik/react-crud"
import type { Document, RecordMedia } from "../types"
import { API_URL } from "../constants"
import { useAuth } from "../auth"
import { useCallback } from "react"
import { normalizeParams } from "@inmagik/react-crud/dist/crud/utils"

export function useDocumentsList(params: Record<string, string> = {}) {
  return useList<Document>(`${API_URL}/admin/records`, {
    query: params,
  })
}

export function useDocument(id: number | undefined) {
  return useDetail<Document>(`${API_URL}/admin/records`, id?.toString())
}

export function useCreateDocument() {
  return useCreate<Document>(`${API_URL}/admin/records`)
}

export function useUpdateDocument() {
  return useUpdate<Document>(`${API_URL}/admin/records`)
}

export function useDeleteDocument() {
  return useDelete(`${API_URL}/admin/records`)
}

export function usePartialUpdateDocument() {
  return usePartialUpdate<Document>(`${API_URL}/admin/records`)
}

export function useDeleteMultiDocument() {
  return useAction(`${API_URL}/admin/records/delete_multi/`, {
    method: "POST",
    responseType: "text",
  })
}

export function useMediaListDocument(id: number | undefined, params: Record<string, string> = {}) {
  return usePlainList<RecordMedia>(`${API_URL}/admin/records/${id}/record-media`, {
    query: params,
  })
}

export function useCreateMediaDocument(id: number | undefined) {
  return useCreate<RecordMedia>(`${API_URL}/admin/records/${id}/create-record-media`, {
    encode: "formdata",
  })
}

export function useDeleteMediaDocument() {
  const auth = useAuth()
  return useCallback((id: number, idMedia: number) => {
    return fetchApi(`${API_URL}/admin/records/${id}/delete-record-media/${idMedia}/`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
        "Content-Type": "application/json",
      },
    }) as Promise<void>
  }, [])
  //  return useDelete(`${API_URL}/admin/records/${id}/delete-record-media/${idMedia}`)
}

export function usePartialUpdateMediaDocument() {
  const auth = useAuth()
  return useCallback((id: number, idMedia: number, values: Partial<RecordMedia>) => {
    return fetchApi(`${API_URL}/admin/records/${id}/update-record-media/${idMedia}/`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(values),
    }) as Promise<RecordMedia>
  }, [])
}

export function useDocumentLazy() {
  const auth = useAuth()
  return useCallback((id: number) => {
    return fetchApi(`${API_URL}/admin/records/${id}`, {
      method: "GET",
      responseType: "json",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
      },
    }) as Promise<Document>
  }, [])
}

export function useDocumentsLazy(params: Record<string, string | number | Array<number>> = {}) {
  const auth = useAuth()
  return useCallback((paramsOverride: Record<string, string | number | Array<number>> = {}) => {
    const combinedParams = { ...params, ...paramsOverride }
    const qs = normalizeParams(combinedParams).toString()
    return fetchApi(`${API_URL}/admin/records?${qs}`, {
      method: "GET",
      responseType: "json",
      headers: {
        Authorization: `Bearer ${auth.tokens?.access}`,
      },
    }) as Promise<{ results: Document[] }>
  }, [])
}
